// Integrations + DB + RDBMS API — extracted from api.js (Tier 2 #9a round 2).
// App integrations, org/app DB connections, table/schema introspection,
// foreign-key lookups, and RDBMS templates (both legacy /rdbms-templates and
// per-app uuid-keyed under /apps/:appId/rdbms-templates/:uuid).

import api from './api';

// ─── App Integrations ──────────────────────────────────────────────────────
export const fetchAppIntegrations = async (appId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/integrations`, { signal });
  return response.data;
};

export const fetchAppIntegrationsByType = async (appId, type) => {
  const response = await api.get(`/apps/${appId}/integrations?type=${type}`);
  return response.data;
};

export const fetchOrgIntegrationsByType = async (orgId, type) => {
  const response = await api.get(`/orgs/${orgId}/integrations`, { params: { type } });
  return response.data;
};

export const createAppIntegration = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/integrations`, data);
  return response.data;
};

export const updateAppIntegration = async (appId, integrationUuid, data) => {
  const response = await api.put(`/apps/${appId}/integrations/${integrationUuid}`, data);
  return response.data;
};

export const deleteAppIntegration = async (appId, integrationUuid) => {
  const response = await api.delete(`/apps/${appId}/integrations/${integrationUuid}`);
  return response.data;
};

// Verify SMTP credentials in `data` (current form config) without sending an
// email. Returns { success: boolean, error?: string }.
export const testAppIntegration = async (appId, integrationUuid, data) => {
  const response = await api.post(`/apps/${appId}/integrations/${integrationUuid}/test`, data);
  return response.data;
};

// ─── DB Connections ────────────────────────────────────────────────────────
export const fetchDBConnections = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/db-connections`);
  return response.data;
};

export const createDBConnection = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/db-connections`, data);
  return response.data;
};

export const updateDBConnection = async (connId, data) => {
  const response = await api.put(`/db-connections/${connId}`, data);
  return response.data;
};

export const deleteDBConnection = async (connId) => {
  const response = await api.delete(`/db-connections/${connId}`);
  return response.data;
};

export const testDBConnection = async (data) => {
  const response = await api.post(`/db-connections/test`, data);
  return response.data;
};

// ─── DB Introspection ──────────────────────────────────────────────────────
export const fetchDBTables = async (connId) => {
  const response = await api.get(`/db-connections/${connId}/tables`);
  return response.data;
};

export const fetchTableSchema = async (connId, tableName) => {
  const response = await api.get(
    `/db-connections/${connId}/tables/${tableName}`,
  );
  return response.data;
};

export const fetchForeignKeys = async (connId) => {
  const response = await api.get(`/db-connections/${connId}/foreign-keys`);
  return response.data;
};

// introspectQueryColumns runs a query template's base_query with a false
// predicate against the connection and returns the projected result columns
// (same ColumnInfo shape as fetchTableSchema). Used by the query-template
// wizard's "detect columns" action. Postgres-only.
export const introspectQueryColumns = async (connId, baseQuery) => {
  const response = await api.post(`/db-connections/${connId}/query-columns`, {
    base_query: baseQuery,
  });
  return response.data;
};

// ─── RDBMS Templates ───────────────────────────────────────────────────────
export const createRDBMSTemplate = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/rdbms-templates`, data);
  return response.data;
};

export const fetchRDBMSTemplates = async (appId) => {
  const response = await api.get(`/apps/${appId}/rdbms-templates`);
  return response.data;
};

// View rows still carry rdbms_template_id as an integer FK column (not a
// JSONB ref the rewriter could uuid-pair). After Z.4/3 deletes the legacy
// /rdbms-templates/:templateId route, this helper resolves uuid via the
// per-app list and forwards to the uuid endpoint. Editor flows are
// low-frequency so the extra list fetch is fine.
export const fetchRDBMSTemplate = async (appId, templateId) => {
  const list = await fetchRDBMSTemplates(appId);
  const template = (list || []).find(t => t.id === Number(templateId));
  if (!template) {
    throw new Error(`RDBMS template ${templateId} not found in app ${appId}`);
  }
  return fetchRDBMSTemplateByUUID(appId, template.uuid);
};

// ─── RDBMS Templates (UUID-keyed under /apps/:appId/) ──────────────────────
export const fetchRDBMSTemplateByUUID = async (appId, templateUuid) => {
  const response = await api.get(`/apps/${appId}/rdbms-templates/${templateUuid}`);
  return response.data;
};
export const updateRDBMSTemplateByUUID = async (appId, templateUuid, data) => {
  const response = await api.put(`/apps/${appId}/rdbms-templates/${templateUuid}`, data);
  return response.data;
};
export const deleteRDBMSTemplateByUUID = async (appId, templateUuid) => {
  const response = await api.delete(`/apps/${appId}/rdbms-templates/${templateUuid}`);
  return response.data;
};
